import { Card, IconButton, Paper, Typography, CircularProgress } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box } from "@mui/system";
import DeleteConfirmDialog from "./commentsDialog/DeleteConfirmDialog";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setDisplayErrorSnackBar } from "../../features/userSlice";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import { formatDate } from '../../utils/helpers';

interface Props {
  isDragging: boolean;
  setIsDragging: any;
  handleFileDrop: any;
  handleFileUpload: any;
  fetchedFiles: any;
  handleRemoveFile: any;
  fileUploadLoading: boolean;
  setFileUploadLoading: any;
}
const Attachments = ({
  isDragging,
  setIsDragging,
  handleFileUpload,
  fetchedFiles,
  handleRemoveFile,
  fileUploadLoading,
}: Props) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previousUploadLoading, setPreviousUploadLoading] = useState(false);
  const [uploadTimer, setUploadTimer] = useState<NodeJS.Timeout | null>(null);

  const { uploadFileTrackID, downloadFileTrackID, previewFileTrackID } = useSelector(
    (state: any) => state.genericCommentsAndFiles
  );
  const dispatch = useDispatch();

  // Function to handle confirm delete
  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleteDialogOpen(false);
    setDeletingFileId(fileToDelete); // Set the specific file being deleted
    try {
      await handleRemoveFile(fileToDelete); // Calls the delete ep from useComments
      setFileToDelete("");
    } catch (error) {
      console.error("Failed to delete file", error);
    } finally {
      setDeletingFileId(null); // Clear the deleting state
    }
  };

  // Function to open the delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setFileToDelete(id);
    setDeleteDialogOpen(true);
  };

  const sortedFetchedFiles =
    fetchedFiles?.length > 0
      ? fetchedFiles
        .filter((file: any) => file.file && file.modified) // Ensure file and modified exist
        .slice()
        .sort(
          (a: any, b: any) =>
            new Date(b.modified).getTime() - new Date(a.modified).getTime()
        )
      : [];

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      
      // Validate file types and sizes
      for (const file of newFiles) {
        // Check file type first
        if (!isAllowedFileType(file)) {
          dispatch(
            setDisplayErrorSnackBar({
              message: `File type not supported. Only PNG, JPG, JPEG, and PDF files are allowed.`,
              type: "error",
            })
          );
          continue;
        }
        
        // Check file size
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > 5.0) {
          dispatch(
            setDisplayErrorSnackBar({
              message: `File exceeds the 5MB limit and unable to upload.`,
              type: "error",
            })
          );
          continue;
        }
        validFiles.push(file);
      }
      
      if (validFiles.length === 0) {
        e.target.value = "";
        return;
      }
      
      // FIX: Append new files to existing selected files instead of replacing
      setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
      
      // Clear any existing timer
      if (uploadTimer) {
        clearTimeout(uploadTimer);
      }
      
      // Set new timer for auto-upload after 2 seconds
      const timer = setTimeout(() => {
        handleUploadSelectedFiles([...selectedFiles, ...validFiles]);
      }, 2000);
      
      setUploadTimer(timer);
    }
    // Clear the input value to allow re-uploading the same file
    e.target.value = "";
  };

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    setIsDragging(false);

    const newFiles = Array.from(event.dataTransfer.files) as File[];
    const validFiles: File[] = [];
    
    // Validate file types and sizes (same as handleFileInputChange)
    for (const file of newFiles) {
      // Check file type first
      if (!isAllowedFileType(file)) {
        dispatch(setDisplayErrorSnackBar({message: `File type not supported. Only PNG, JPG, JPEG, and PDF files are allowed.`,type: "error",}));
        continue;
      }
      
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 5.0) {
        dispatch(setDisplayErrorSnackBar({message: `File exceeds the 5MB limit and unable to upload.`,type: "error",}));
        continue;
      }
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    // Append new files to existing selected files
    setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
    
    // Clear any existing timer
    if (uploadTimer) {
      clearTimeout(uploadTimer);
    }
    
    // Set new timer for auto-upload after 2 seconds
    const timer = setTimeout(() => {
      handleUploadSelectedFiles([...selectedFiles, ...validFiles]);
    }, 2000);
    setUploadTimer(timer);
  };

  const handleUploadSelectedFiles = (filesToUpload = selectedFiles) => {
    if (filesToUpload.length > 0) {
      const event = {
        target: {
          files: filesToUpload
        }
      };
      handleFileUpload(event);
    }
  };

  // Clear selected files when upload loading finishes
  useEffect(() => {
    if (previousUploadLoading && !fileUploadLoading && selectedFiles.length > 0) {
      setSelectedFiles([]);
    }
    setPreviousUploadLoading(fileUploadLoading);
  }, [fileUploadLoading, previousUploadLoading, selectedFiles.length]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (uploadTimer) {
        clearTimeout(uploadTimer);
      }
    };
  }, [uploadTimer]);

  // Function to handle file download
  const handleFileDownload = async (file: any) => {
    setDownloadingFileId(file.shared_uid);
    try {
      const link = document.createElement('a');
      link.href = file.downloadable_url;
      link.download = file.file.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file", error);
    } finally {
      setTimeout(() => setDownloadingFileId(null), 1000); // Clear loading after 1 second
    }
  };

  const handleFilePreview = async (file: any) => {
    window.open(file?.file, '_blank');
  }

  // Helper function to check if file type is allowed
  const isAllowedFileType = (file: File) => {
    const allowedTypes = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return allowedTypes.includes(fileExtension);
  };

  return (
    <>
      <Box>
        {/* <Typography>Attachments</Typography> */}
        <Card sx={{ marginTop: "16px", border: "none", borderRadius: 0, boxShadow: "none" }}>
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              // margin: 2,
              padding: 4,
              backgroundColor: "#ffffff",
              border: isDragging ? "1px dashed #9C27B0" : "1px dashed #cacacaff",
              opacity: (fileUploadLoading || selectedFiles.length > 0) ? 0.5 : 1, // Dim when loading or files selected
              pointerEvents: (fileUploadLoading || selectedFiles.length > 0) ? "none" : "auto", // Disable interactions when loading or files selected
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (selectedFiles.length === 0 && !fileUploadLoading) {
                setIsDragging(true);
              }
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={selectedFiles.length === 0 && !fileUploadLoading ? handleFileDrop : undefined}
          >
            <UploadFileIcon color="secondary" fontSize="large" />
            <Typography
              component="label"
              sx={{ display: "flex", gap: 1, cursor: (selectedFiles.length > 0 || fileUploadLoading) ? "not-allowed" : "pointer" }}
            >
              <Typography
                color="secondary"
                sx={{ textDecoration: "underline", cursor: (selectedFiles.length > 0 || fileUploadLoading) ? "not-allowed" : "pointer" }}
              >
                Click to upload
              </Typography>
              <input
                data-tracker={uploadFileTrackID}
                type="file"
                multiple
                accept=".png, .jpg, .jpeg, .pdf"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
                aria-label="Upload files"
                disabled={selectedFiles.length > 0 || fileUploadLoading}
              />
              or drag and drop
            </Typography>
            <Typography sx={{ color: "#9e9e9e" }}>
              PNG, JPG or PDF (max. 5MB)
            </Typography>
          </Paper>
        </Card>
        {/* Display selected file details */}
        {(selectedFiles.length > 0 || fileUploadLoading) && (
          <Box sx={{ mt: 2 }}>
            {selectedFiles.filter(isAllowedFileType).map((file, index) => (
              <Box key={`${file.name}-${file.size}-${index}`} display="flex" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mt: 1, px: 2, pt: 2 }}>
                <Box display="flex" alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                  <IconButton
                    color="secondary"
                    size="small"
                    sx={{ mr: 2, p: 0.5, mt: -0.25 }}
                    disabled
                  >
                    <FileDownloadOutlinedIcon
                      data-tracker={downloadFileTrackID}
                      fontSize="small"
                    />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    size="small"
                    sx={{ mr: 2, p: 0.5, mt: -0.25 }}
                    disabled
                  >
                    <PreviewOutlinedIcon
                      fontSize="small" data-tracker={previewFileTrackID}
                    />
                  </IconButton>
                  <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" noWrap sx={{ lineHeight: 1.2 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(2)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`} • {fileUploadLoading ? 'Uploading...' : 'Please wait...'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {/* <IconButton size="small" sx={{"&:hover": { cursor: "pointer" } }} onClick={() => setSelectedFiles([])}>
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton> */}
                    {fileUploadLoading && <CircularProgress size={24} color="secondary" />}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {/* Display uploaded files */}
        {sortedFetchedFiles?.map((file: any, index: number) => (
          <Box key={index} display="flex" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mt: 1, px: 2, pt: 2 }}>
            <Box display="flex" alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
              {downloadingFileId === file.shared_uid ? (
                <Box sx={{ mr: 2, p: 0.5, mt: -0.25, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color="secondary" />
                </Box>
              ) : (
                <>
                <IconButton
                  color="secondary"
                  size="small"
                  sx={{ mr: 2, p: 0.5, mt: -0.25 }}
                  onClick={() => handleFileDownload(file)}
                  disabled={deletingFileId === file.shared_uid || fileUploadLoading || selectedFiles.length > 0}
                >
                  <FileDownloadOutlinedIcon
                    data-tracker={downloadFileTrackID}
                    fontSize="small"
                  />
                </IconButton>
                <IconButton
                  color="secondary"
                  size="small"
                  sx={{ mr: 2, p: 0.5, mt: -0.25 }}
                  onClick={() => handleFilePreview(file)}
                  disabled={deletingFileId === file.shared_uid || fileUploadLoading || selectedFiles.length > 0}
                >
                  <PreviewOutlinedIcon
                    fontSize="small" data-tracker={previewFileTrackID}
                  />
                </IconButton>
                </>
              )}
              <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" noWrap sx={{ lineHeight: 1.2 }}>
                  {file.file.split("/").pop()?.replace(/['"]/g, '') || 'Unknown file'}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  {file.size ? (file.size > 1024 ? `${(file.size / 1024).toFixed(2)} MB` : `${file.size} KB`) : 'Size unknown'} • Uploaded on {formatDate(file.created)}
                </Typography>
              </Box>
            </Box>
            <Box>
              {deletingFileId === file.shared_uid ? (
                <CircularProgress size={24} color="secondary" />
              ) : (
                <IconButton
                  size="small"
                  sx={{
                    color: "#9C27B0",
                    "&:hover": {
                      color: "#9C27B0",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => openDeleteDialog(file.shared_uid)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleConfirmDelete}
        deleteFrom="file"
      />
    </>
  );
};

export default Attachments;
